<template>
  <!-- RQ "Payment Receipt" acknowledgement (Android PaymentInfoDialog): the tech confirms the cash / check / other
       payment, or enters the Atwood revised total. Not closable (no ✕, static backdrop) — a decision is required. -->
  <div ref="rqPaymentAckModal" class="modal fade fxModal fxConfirm" tabindex="-1" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body">
          <ConfirmLayout title="Payment Receipt" :closable="false">
            <p class="m-0 text-[16px] leading-[22px] text-[#333333] max-h-[40vh] overflow-y-auto">{{ description }}</p>

            <!-- check: front / back images -->
            <div v-if="type === 'check' && checks.length" class="mt-[15px] flex justify-center gap-[10px]">
              <button v-for="(c, i) in checks.slice(0, 2)" :key="c.key || i" type="button"
                class="flex-1 max-w-[180px] border-0 bg-transparent p-0 flex flex-col items-center gap-[4px]"
                @click="openUrl(c.url)">
                <img :src="c.url" :alt="c.name || 'Check'"
                  class="w-full h-[100px] rounded-[8px] object-cover border border-[#E1E5EF]">
                <span class="text-[12px] text-[#999999]">{{ c.name || (i ? 'Back' : 'Front') }}</span>
              </button>
            </div>

            <!-- atwood: revised total -->
            <div v-if="type === 'atwood'" class="mt-[15px] flex flex-col gap-[10px] text-left text-[14px]">
              <div class="flex items-center justify-between gap-[10px]">
                <span class="text-[#666666]">Job's Total:</span>
                <span class="font-medium text-[#333333]">{{ data.amount_prefix }}</span>
              </div>
              <div>
                <span class="uqcLabel">Atwood Revised Amount:</span>
                <inputComponent v-model="revisedAmount" placeholder="$" inputmode="decimal" :custom-class="fieldClass" />
              </div>
            </div>

            <!-- other: institution + notes -->
            <div v-if="type === 'other'" class="mt-[15px] flex flex-col gap-[8px] text-left text-[14px]">
              <div><span class="text-[#666666]">Financial Institution Name : </span>{{ data.institution_name || '' }}</div>
              <div class="whitespace-pre-line break-words"><span class="text-[#666666]">Notes : </span>{{ data.description || '' }}</div>
            </div>

            <template #actions>
              <button v-if="type !== 'atwood'" type="button" class="fxConfirmBtnOutline" @click="decide('no')">
                Not received
              </button>
              <button type="button" class="fxConfirmBtn" @click="type === 'atwood' ? proceedAtwood() : decide('yes')">
                {{ type === 'atwood' ? 'Proceed with Atwood' : 'Received' }}
              </button>
            </template>
          </ConfirmLayout>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import ConfirmLayout from "../confirmDialog/ConfirmLayout.vue";
import inputComponent from "../partialComponent/inputComponent.vue";
import { rqApi } from "@/services/rqApi";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";

const toast = useToast();

export default {
  components: { ConfirmLayout, inputComponent },
  props: {
    orderId: { type: [Number, String], required: true },
    consumerName: { type: String, default: "" },
  },
  emits: ["changed"],
  data() {
    return {
      data: {},
      revisedAmount: "",
      fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333]",
    };
  },
  computed: {
    type() {
      return String(this.data.payment_type || "").toLowerCase();
    },
    checks() {
      return Array.isArray(this.data.check) ? this.data.check : [];
    },
    // Android PaymentInfoDialog texts, word for word
    description() {
      const name = this.consumerName;
      const amount = this.data.amount_prefix || "";
      return {
        check: `${name} has selected check as the preferred payment option. Please confirm if you have received a check for sum of ${amount} and the images attached, are of the same check as received.`,
        cash: `${name} has selected Cash as the preferred payment option. Please confirm if you have received the sum of ${amount}`,
        atwood: `${name} has selected Atwood as the preferred payment option. Please enter the revised job total and hit Proceed to update the invoice.`,
        other: `${name} has selected Other as the preferred payment option. Please review the details below and acknowledge if you want to proceed.`,
      }[this.type] || "";
    },
    typeLabel() {
      return this.type ? this.type.charAt(0).toUpperCase() + this.type.slice(1) : "";
    },
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.rqPaymentAckModal, { backdrop: "static", keyboard: false });
    this.$refs.rqPaymentAckModal.addEventListener("hidden.bs.modal", enableScroll);
  },
  beforeUnmount() {
    this.$refs.rqPaymentAckModal?.removeEventListener("hidden.bs.modal", enableScroll);
    this.bsModal?.dispose();
  },
  methods: {
    async open() {
      const res = await rqApi.paymentData(this.orderId);
      if (!res.ok) return toast.error(res.message || "Unable to load the payment details.");
      // no pending payment: the server answers an empty object
      if (!res.body?.payment_type) return;
      this.data = res.body;
      this.revisedAmount = this.data.amount_prefix || "";
      disableScroll();
      this.bsModal.show();
    },
    openUrl(url) {
      if (url) window.open(url, "_blank", "noopener");
    },
    decide(decision) {
      this.$dialog.show({
        title: `Payment via ${this.typeLabel}`,
        text: "Do you want to proceed?",
        buttonText: "Yes",
        onConfirm: async () => this.done(await rqApi.paymentDecision(this.orderId, decision, this.data.event)),
      });
    },
    proceedAtwood() {
      const amount = String(this.revisedAmount || "").replace(/[$,\s]/g, "");
      if (!amount || isNaN(Number(amount))) return toast.error("Please enter the revised amount.");
      this.$dialog.show({
        title: "Payment via Atwood",
        text: "The revised total can not be changed later. If you wish to edit the revise total, then hit NO, otherwise hit YES to proceed.",
        buttonText: "Yes",
        onConfirm: async () => this.done(await rqApi.paymentAtwood(this.orderId, Number(amount), this.data.event)),
      });
    },
    // Android closes and refreshes the order on success and on a server error alike (the state may have moved on)
    done(res) {
      if (!res.ok) toast.error(res.message || "Sorry we are unable to complete your request at the time");
      this.$emit("changed");
      this.bsModal.hide();
    },
  },
};
</script>
